import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { homeSummaryApi, TitleQcSummary } from "../../../../api/homeSummaryApi";
import { ROUTES } from "../../../../config/routes";
import { WidgetFilterValues } from "../../prefs/homePrefs";
import { WidgetComponentProps, getScope, getStringArray, scopeParams } from "../types";
import { useWidgetData } from "../useWidgetData";
import {
  CardEmpty,
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  countTone,
  formatNumber,
  formatShortDate,
} from "../ui";

/** Title QC defaults to "my issues"; `myIssues=0` opens the full queue. */
export const titleQcLink = (filters: WidgetFilterValues): string =>
  getScope(filters) === "mine"
    ? ROUTES.METADATA_AUDIT_TITLE_QC
    : `${ROUTES.METADATA_AUDIT_TITLE_QC}?myIssues=0`;

const TitleQcCard: React.FC<WidgetComponentProps> = ({ access, filters, onOptions }) => {
  const requestParams = useMemo(
    () => ({
      ...scopeParams(filters),
      videoTypes: getStringArray(filters, "videoTypes").join(","),
      languages: getStringArray(filters, "languages").join(","),
      priorities: getStringArray(filters, "priorities").join(","),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)]
  );

  const { data, loading, error, reload } = useWidgetData<TitleQcSummary>(
    `title-qc:${JSON.stringify(requestParams)}`,
    () => homeSummaryApi.getTitleQc(requestParams)
  );

  useEffect(() => {
    if (!data) return;
    onOptions("videoTypes", data.options.videoTypes.map((v) => ({ label: v, value: v })));
    onOptions("languages", data.options.languages.map((v) => ({ label: v, value: v })));
  }, [data, onOptions]);

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const scope = getScope(filters);
  const link = titleQcLink(filters);
  const run = data.latestRun;

  if (data.openIssues === 0 && scope === "mine") {
    return (
      <>
        <CardEmpty
          title="No titles waiting for your review"
          body={`${formatNumber(data.networkOpenIssues)} open across the network.`}
          action={
            <Link className="home-link" to={titleQcLink({ scope: "all" })}>
              View the full queue
            </Link>
          }
        />
        {run && <Freshness label="Last run" at={run.dateCreated} />}
      </>
    );
  }

  const videoTypes = Object.entries(data.byVideoType).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label={scope === "mine" ? "Your titles to review" : "Titles to review"}
          value={data.openIssues}
          tone={countTone(data.openIssues)}
          to={link}
          emphasis
          hint={
            scope !== "all"
              ? `of ${formatNumber(data.networkOpenIssues)} network-wide`
              : undefined
          }
        />
        <Kpi
          label="High priority"
          value={data.highPriority}
          tone={countTone(data.highPriority, "bad")}
          hint="Spelling or prohibited terms"
          to={link}
        />
        <Kpi
          label="Needs manual review"
          value={data.needsManualReview}
          tone={countTone(data.needsManualReview)}
          hint="Too long or no substitute"
          to={link}
        />
      </KpiGrid>

      {videoTypes.length > 0 && (
        <div className="home-pills">
          {videoTypes.map(([type, count]) => (
            <span key={type} className="home-pill">
              {type} <strong>{formatNumber(count)}</strong>
            </span>
          ))}
        </div>
      )}

      <div className="home-stat-row" title="Totals across the whole network, not just this card's filters">
        <span className="home-muted">Network-wide:</span>
        <span>
          <i className="pi pi-check" /> {formatNumber(data.acceptedTotal)} accepted
        </span>
        <span>
          <i className="pi pi-times" /> {formatNumber(data.rejectedTotal)} rejected
        </span>
        {/* Admin audit only: failures managers can fix are sent back to their queue. */}
        {access.isAdmin && data.applyFailedTotal > 0 && (
          <Link className="home-text-bad" to={`${ROUTES.METADATA_AUDIT_TITLE_QC}?audit=failedUpdates`}>
            <i className="pi pi-exclamation-circle" /> {formatNumber(data.applyFailedTotal)} failed to update on YouTube
          </Link>
        )}
      </div>

      {run && (
        <div className="home-card__meta">
          <span className="home-muted">
            Last run {formatShortDate(run.dateCreated)}
            {run.openIssuesWritten != null && ` · ${formatNumber(run.openIssuesWritten)} issues found`}
            {run.status !== "completed" && ` · ${run.status}`}
          </span>
          <Freshness at={data.generatedAt} />
        </div>
      )}
    </>
  );
};

export default TitleQcCard;
